import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API}/api`,
  withCredentials: true,
});

// If an impersonation token exists in sessionStorage, send it as Authorization header
// This allows impersonated tabs to auth independently from the admin's cookies.
// AUSNAHME: /auth/login, /auth/register, /auth/logout, /auth/2fa/* dürfen niemals
// mit Impersonation-Bearer laufen — sonst würde ein regulärer Neu-Login noch als
// alter impersonierter User durchgehen und Bearer priorisiert Cookies im Backend.
api.interceptors.request.use((config) => {
  const url = String(config.url || "");
  const isAuthEndpoint = /^\/?auth\/(login|register|logout|2fa\/)/.test(url) || /\/auth\/(login|register|logout|2fa\/)/.test(url);
  if (isAuthEndpoint) return config;
  const impToken = sessionStorage.getItem("st_impersonate_token");
  if (impToken) {
    config.headers.Authorization = `Bearer ${impToken}`;
  }
  return config;
});

// iter281 · Stiller Session-Refresh: läuft der 60-Min-Access-Token ab, holt ein
// 401-Response-Interceptor EINMAL still einen neuen Access-Token über den 3-Tage-
// Refresh-Cookie (/auth/refresh) und wiederholt die ursprüngliche Anfrage. So bleibt
// man bei Aktivität eingeloggt (Sliding Session, max. 3 Tage Inaktivität).
// Impersonation-Tabs (Bearer-Token) werden ausgenommen — sie nutzen keine Cookies.
const AUTH_SKIP_RE = /\/auth\/(login|register|logout|refresh|2fa\/)/;
let refreshPromise = null;

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = String(original.url || "");
    const isImpersonating = !!sessionStorage.getItem("st_impersonate_token");
    if (status !== 401 || original._retry || AUTH_SKIP_RE.test(url) || isImpersonating) {
      return Promise.reject(error);
    }
    original._retry = true;
    try {
      if (!refreshPromise) {
        refreshPromise = axios
          .post(`${API}/api/auth/refresh`, {}, { withCredentials: true })
          .finally(() => { refreshPromise = null; });
      }
      await refreshPromise;
      return api(original); // Original-Request mit frischem Cookie wiederholen
    } catch (refreshErr) {
      return Promise.reject(error);
    }
  }
);

export function formatError(err) {
  // iter324 · Infrastruktur-/Gateway-Fehler (Cloudflare 5xx, leere/HTML-Antwort) NICHT
  // roh anzeigen → "" zurückgeben, damit der Aufrufer seinen freundlichen Fallback-Text zeigt.
  const status = err?.response?.status;
  const rawData = err?.response?.data;
  const rawStr = typeof rawData === "string" ? rawData : "";
  const looksInfra =
    (typeof status === "number" && status >= 502 && status <= 599) ||
    /cloudflare|origin web server|<!doctype|<html|could not parse/i.test(rawStr) ||
    /status code 5\d\d|network error|timeout/i.test(err?.message || "");
  if (looksInfra) return "";

  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  // Sub-Batch 4c.5.4: friendly ACCOUNT_SUSPENDED message
  if (d?.code === "ACCOUNT_SUSPENDED") {
    let untilStr = "";
    if (d.suspended_until) {
      try {
        untilStr = new Date(d.suspended_until).toLocaleString("de-DE", { dateStyle: "long", timeStyle: "short" });
      } catch { untilStr = String(d.suspended_until); }
    }
    const base = d.message || "Dein Konto ist derzeit gesperrt. Senden ist während der Sperrzeit nicht möglich.";
    const reason = d.suspension_reason ? ` Grund: ${d.suspension_reason}.` : "";
    const until = untilStr ? ` Die Sperre endet am ${untilStr}.` : "";
    // Avoid double-appending if base already contains reason/until
    if (base.includes("Grund") || base.includes("Sperre endet")) return base;
    return base + reason + until;
  }
  if (d?.primary_reason_text) return d.primary_reason_text;
  if (d?.message) return d.message;
  if (d?.msg) return d.msg;
  if (d?.code) return d.code;
  try { return JSON.stringify(d); } catch { return String(d); }
}

export default api;
